import { pathOr, merge, pipe, map, keys, path, flatten, assoc, defaultTo, equals, over, lensPath, values, addIndex, reduce, max, slice, min, both, propSatisfies, complement, isNil, isEmpty, has, prop, mergeAll } from 'ramda';
import { bind } from 'redux-effects';
import { fetchPlayerLimits, cancelPlayerLimit, requestPlayerLimit } from '@rushplay/api-client';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';
import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { SESSION_EXPIRED } from '@rushplay/websockets';

var TIMESTAMP = 'EFFECT_TIMESTAMP';

function timestamp() {
  return { type: TIMESTAMP };
}

/**
 * Create middleware for declarative current timestamp retrieval.
 * Designed to be used with `redux-effects`.
 *
 * @param {function(): number} getTimestamp - Timestamp getter, e.g `Date.now`
 * @returns Redux middleware
 */
function createTimestampMiddleware(getTimestamp) {
  return function () {
    return function (next) {
      return function (action) {
        return action.type === TIMESTAMP ? Promise.resolve(getTimestamp()) : next(action);
      };
    };
  };
}

var index = createTimestampMiddleware(Date.now.bind(Date));

/**
 * @constant
 * @memberof rushplay/compliance/limits
 */
var APPLIED = 'compliance/limits/APPLIED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var CLEARED = 'compliance/limits/CLEARED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var DECREASED = 'compliance/limits/DECREASED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var EDITING_TOGGLED = 'compliance/limits/EDITING_TOGGLED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var INCREASED = 'compliance/limits/INCREASED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var LOADED = 'compliance/limits/LOADED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var REVERTED = 'compliance/limits/REVERTED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var SAVED = 'compliance/limits/SAVED';
/**
 * @constant
 * @memberof rushplay/compliance/limits
 */

var UNSET = 'compliance/limits/UNSET';

function toLimit(target, period, limits) {
  var limit = pathOr({}, [target, period], limits);
  return merge(limit, {
    target: target,
    period: period
  });
}
/**
 * Stores limits response from fetchPlayerLimits to state
 * @memberof rushplay/compliance/limits
 * @param {Object} limits Limits
 * @returns {Object} Redux action
 */


function load(limit) {
  return {
    type: LOADED,
    payload: limit,
    error: limit instanceof Error
  };
}
/**
 * Decrease limit amount; applied immediately
 * @private
 * @param {string} target Type
 * @param {string} period Period
 * @param {number} nextValueCents Amount
 * @returns {Object} Redux action
 */

function decrease(target, period, nextValueCents) {
  return {
    type: DECREASED,
    payload: {
      target: target,
      period: period,
      nextValueCents: nextValueCents
    }
  };
}
/**
 * Set next limit amount which will take effect
 * after timeout; decreases applied immediately
 * @private
 * @param {string} target Type
 * @param {string} period Period
 * @param {number} activeFrom Timestamp
 * @param {number} valueCents Limit value in cents
 * @returns {Object} Redux action
 */


function increase(target, period, activeFrom, nextValueCents) {
  return {
    type: INCREASED,
    payload: {
      target: target,
      period: period,
      activeFrom: activeFrom,
      nextValueCents: nextValueCents
    }
  };
}
/**
 * Attempts to remove limit, if remote removal fails, local changes will be
 * rolled back.
 * @private
 * @param {string} target Type
 * @param {string} period Period
 * @param {number} activeFrom Timestamp
 * @returns {Object} Redux action
 */


function unset(target, period, activeFrom) {
  return {
    type: UNSET,
    payload: {
      target: target,
      period: period,
      activeFrom: activeFrom
    }
  };
}
/**
 * Toggle editing of the limit
 * @memberof rushplay/compliance/limits
 * @param {Object} limit Original limit
 * @param {boolean} [value] Force isEditing to be set to provided value
 * @returns {Object} Redux action
 */


function toggleEdit(limit, value) {
  return {
    type: EDITING_TOGGLED,
    payload: {
      target: limit.target,
      period: limit.period,
      value: value
    }
  };
}
/**
 * Perform necessary type of change on a limit
 * @memberof rushplay/compliance/limits
 * @param {Object} limit Original limit
 * @param {Object} nextLimit Desired limit
 * @returns {Object} Redux action
 */

function set(limit, nextLimit) {
  if (nextLimit.valueCents === null) {
    return unset(limit.target, limit.period, nextLimit.activeFrom);
  }

  if (nextLimit.valueCents > limit.valueCents) {
    return increase(limit.target, limit.period, nextLimit.activeFrom, nextLimit.valueCents);
  }

  return decrease(limit.target, limit.period, nextLimit.valueCents);
}
/**
 * Stores response from a successful request to state.
 * @memberof rushplay/compliance/limits
 * @returns {Object} Redux action
 */

function fetch() {
  return fetchPlayerLimits({
    success: pipe(pathOr({}, ['value', 'result']), function (limits) {
      return map(function (target) {
        return map(function (period) {
          return toLimit(target, period, limits);
        }, keys(path([target], limits)));
      }, keys(limits));
    }, flatten, map(load)),
    failure: function failure() {
      return load(new Error('player-limits.errors.failed-loading'));
    },
    cacheFor: 2000,
    version: 2
  });
}
/**
 * Removes pending limit update locally and remotely. If remote operation fails,
 * local state changes will be rolled back
 * @memberof rushplay/compliance/limits
 * @param {Object} limit Limit
 * @returns {Object} Redux action
 */

function revert(limit) {
  return [{
    type: REVERTED,
    payload: {
      target: limit.target,
      period: limit.period
    }
  }, cancelPlayerLimit(limit.target, limit.period, {
    success: function success(res) {
      return load(toLimit(limit.target, limit.period, res.value.result));
    },
    failure: function failure() {
      var error = new Error('player-limits.errors.cancel-limit');
      error.variables = {
        target: limit.target,
        period: limit.period
      };
      return [load(error), load(limit)];
    },
    version: 2
  })];
}
/**
 * Performs a post request to update a limit amount, on success it replaces the
 * "optimistically" updated amount with the response, on failure it will
 * rollback to the previous limit.
 * @memberof rushplay/compliance/limits
 * @param {Object} limit Limit
 * @returns {Object} Redux action
 */

function submit(limit, nextValueCents) {
  return [set(limit, {
    activeFrom: Infinity,
    valueCents: nextValueCents
  }), requestPlayerLimit(limit.target, limit.period, nextValueCents, {
    success: function success(res) {
      return load(toLimit(limit.target, limit.period, res.value.result));
    },
    failure: function failure() {
      var error = nextValueCents === null ? new Error('player-limits.errors.remove-limit') : new Error('player-limits.errors.set-limit');
      error.variables = {
        target: limit.target,
        period: limit.period
      };
      return [load(error), load(limit)];
    },
    version: 2
  })];
}
/**
 * Either applies next limit or removes limit on timeout.
 * @memberof rushplay/compliance/limits
 * @param {Object} limit Limit
 * @returns {Object} Redux action
 */

function apply(limit) {
  return bind(timestamp(), function (timestamp$$1) {
    if (limit.activeFrom && timestamp$$1 >= limit.activeFrom) {
      return {
        type: APPLIED,
        payload: {
          target: limit.target,
          period: limit.period
        }
      };
    }
  });
}
/**
 * Clears the limit state
 * @memberof rushplay/compliance/limits
 * @returns {Object} Redux action
 */

function clear() {
  return {
    type: CLEARED
  };
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  }

  return _assertThisInitialized(self);
}

/**
 * Gets limit
 * @private
 * @param {Object} state Limit state
 * @returns {Object} limit Limit
 */

function getRawLimit(state, props) {
  var limit = pathOr({}, [props.target, props.period], state);
  return merge(limit, {
    target: props.target,
    period: props.period
  });
}
/**
 * Gets min and max values for all periods by given limit type
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limits state
 * @param {Object} props
 * @param {string} props.target Limit type
 * @returns {Object} Contains min and max value for each period
 */


function getValidatorsForTarget(state, props) {
  var limits = pathOr({}, [props.target], state);
  var values$$1 = values(map(function (limit) {
    return pathOr(path(['valueCents'], limit), ['nextValueCents'], limit);
  }, limits));
  var mapIndexed = addIndex(map);
  var validators = {};
  mapIndexed(function (period, index) {
    validators = Object.assign(validators, _defineProperty({}, period, {
      min: reduce(max, 0, slice(0, index, values$$1)),
      max: reduce(min, Number.MAX_SAFE_INTEGER, slice(index + 1, values$$1.length, values$$1))
    }));
  }, keys(limits));
  return validators;
}
var nextAmount = createSelector([getRawLimit], function (limit) {
  return limit.nextValueCents;
});
var amount = createSelector([getRawLimit], function (limit) {
  return limit.valueCents;
});
/**
 * Checks if limit is being increased
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @returns {Boolean}
 */

var isIncreased = createSelector([getRawLimit], function (limit) {
  return limit.nextValueCents > limit.valueCents;
});
/**
 * Checks if limit is being removed
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @returns {Boolean}
 */

var isRemoved = createSelector([getRawLimit], both(propSatisfies(complement(isNil), 'activeFrom'), propSatisfies(isNil, 'nextValueCents')));
/**
 * Checks if limit is unset
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @param {Boolean}
 */

var isUnset = createSelector([getRawLimit], function (limit) {
  return isNil(limit.valueCents);
});
/**
 * Checks if limit is loaded
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @param {Object} props
 * @param {String} props.target Limit target
 * @param {String} props.period Limit period
 * @returns {Boolean}
 */

function isLoaded(state, props) {
  return !isEmpty(pathOr({}, [props.target, props.period], state));
}
/**
 * Checks if limit is unset
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @returns {Boolean}
 */

var getLimit = createSelector([getRawLimit, isIncreased, isRemoved, isUnset, isLoaded], function (limit, isIncreased, isRemoved, isUnset, isLoaded) {
  return merge(limit, {
    isRemoved: isRemoved,
    isIncreased: isIncreased,
    isUnset: isUnset,
    isLoaded: isLoaded
  });
});
/**
 * Checks if there is an existing limit based on type
 * @memberof rushplay/compliance/limits
 * @param {Object} state Limit state
 * @param {Object} props
 * @param {String} props.type Limit type
 * @returns {Boolean}
 */

function hasLimit(state, props) {
  return has(props.type, state);
}

/**
 * @typedef {Object} LimitRenderProps
 * @property {number} activeFrom Timestamp (in ms) when limit will take effect
 * @property {number} nextValueCents Next limit value (in cents)
 * @property {("day"|"week"|"month")} period Limit’s period
 * @property {("bet"|"deposit"|"loss"|"loginTime")} target Limit’s type
 * @property {number} valueCents Current limit value (in cents)
 * @property {function} onApply Try to apply limit’s next values
 * @property {function} onRevert Revert pending changes
 * @property {function} onSet Update limit’s value
 * @property {function} onUnset Remove the limit
 */

var Limit =
/*#__PURE__*/
function (_React$PureComponent) {
  _inherits(Limit, _React$PureComponent);

  function Limit(props) {
    var _this;

    _classCallCheck(this, Limit);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Limit).call(this, props));
    _this.handleTimerDestroy = _this.handleTimerDestroy.bind(_assertThisInitialized(_this));
    _this.handleTimerUpdate = _this.handleTimerUpdate.bind(_assertThisInitialized(_this));
    props.onFetch();
    return _this;
  }

  _createClass(Limit, [{
    key: "handleTimerDestroy",
    value: function handleTimerDestroy() {
      if (this.timeoutId) {
        this.timeoutId = void window.clearTimeout(this.timeoutId);
      }
    }
  }, {
    key: "handleTimerUpdate",
    value: function handleTimerUpdate() {
      this.handleTimerDestroy();

      if (this.props.activeFrom) {
        var timeout = this.props.activeFrom - Date.now(); // TODO: redo with redux-effects-timeout

        this.timeoutId = window.setTimeout(this.props.onApply, timeout);
      }
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      this.handleTimerUpdate();
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (prevProps.activeFrom !== this.props.activeFrom) {
        this.handleTimerUpdate();
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.handleTimerDestroy();
      this.props.onEditToggle(false);
    }
  }, {
    key: "render",
    value: function render() {
      return this.props.children({
        activeFrom: this.props.activeFrom,
        editableFrom: this.props.editableFrom,
        isEditing: this.props.isEditing,
        isIncreased: this.props.isIncreased,
        isLoaded: this.props.isLoaded,
        isRemoved: this.props.isRemoved,
        isUnset: this.props.isUnset,
        nextValueCents: this.props.nextValueCents,
        period: this.props.period,
        target: this.props.target,
        valueCents: this.props.valueCents,
        onApply: this.props.onApply,
        onEditToggle: this.props.onEditToggle,
        onRevert: this.props.onRevert,
        onSet: this.props.onSet,
        onUnset: this.props.onUnset
      });
    }
  }]);

  return Limit;
}(React.PureComponent);
Limit.propTypes = {
  activeFrom: PropTypes.number,
  editableFrom: PropTypes.number,
  children: PropTypes.func,
  isEditing: PropTypes.bool,
  isIncreased: PropTypes.bool,
  isLoaded: PropTypes.bool,
  isRemoved: PropTypes.bool,
  isUnset: PropTypes.bool,
  nextValueCents: PropTypes.number,
  period: PropTypes.oneOf(['day', 'week', 'month']).isRequired,
  target: PropTypes.oneOf(['deposit', 'bet', 'loss', 'loginTime']).isRequired,
  valueCents: PropTypes.number,
  onApply: PropTypes.func,
  onEditToggle: PropTypes.func,
  onFetch: PropTypes.func,
  onRevert: PropTypes.func,
  onSet: PropTypes.func,
  onUnset: PropTypes.func
};

function mapStateToProps(state, props) {
  var limitsState = prop(props.mountPoint, state);
  return getLimit(limitsState, {
    target: props.target,
    period: props.period
  });
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    onApply: apply,
    onEditToggle: toggleEdit,
    onFetch: fetch,
    onRevert: revert,
    onSet: submit,
    onUnset: submit
  }, dispatch);
}

function mergeProps(stateProps, dispatchProps, ownProps) {
  return mergeAll([stateProps, {
    onApply: function onApply() {
      return dispatchProps.onApply(stateProps);
    },
    onEditToggle: function onEditToggle(value) {
      return dispatchProps.onEditToggle(stateProps, value);
    },
    onFetch: function onFetch() {
      return dispatchProps.onFetch();
    },
    onRevert: function onRevert() {
      return dispatchProps.onRevert(stateProps);
    },
    onSet: function onSet(amount$$1) {
      return dispatchProps.onSet(stateProps, amount$$1);
    },
    onUnset: function onUnset() {
      return dispatchProps.onUnset(stateProps, null);
    }
  }, ownProps]);
}

var connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);
/**
 * Render prop component to access and manage single limit
 * @memberof rushplay/compliance/limits
 * @type {ReactComponent}
 * @param {Object} props
 * @param {function(LimitRenderProps): ReactElement} props.children
 * @param {("day"|"week"|"month")} period Limit’s period
 * @param {("bet"|"deposit"|"loss")} target Limit’s type
 * @example
 * import { Limit } from '@rushplay/compliance/limits'
 *
 * <Limit
 *   mountPoint="limits"
 *   target="deposit"
 *   period="day"
 * >
 *   {limit => (
 *     <h1>Daily deposit limit</h1>
 *     <div>Currently set to {limit.valueCents / 100}</div>
 *     <button onClick={limit.onUnset}>Remove limit</button>
 *   )}
 * </Limit>
 */

var Limit$1 = connector(Limit);

function limitReducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;

  switch (action.type) {
    case APPLIED:
      {
        return merge(state, {
          activeFrom: null,
          nextValueCents: null,
          valueCents: state.nextValueCents
        });
      }

    case DECREASED:
      {
        return merge(state, {
          activeFrom: null,
          isEditing: false,
          nextValueCents: null,
          valueCents: action.payload.nextValueCents
        });
      }

    case EDITING_TOGGLED:
      {
        if (typeof action.payload.value === 'boolean') {
          return assoc('isEditing', action.payload.value, state);
        } else {
          return assoc('isEditing', !state.isEditing, state);
        }
      }

    case LOADED:
      {
        var nextState = {
          activeFrom: defaultTo(null, action.payload.activeFrom),
          editableFrom: defaultTo(null, action.payload.editableFrom),
          nextValueCents: defaultTo(null, action.payload.nextValueCents),
          valueCents: defaultTo(null, action.payload.valueCents)
        };

        if (equals(state, merge(state, nextState))) {
          return state;
        }

        return nextState;
      }

    case INCREASED:
      {
        return merge(state, {
          activeFrom: action.payload.activeFrom,
          isEditing: false,
          nextValueCents: action.payload.nextValueCents
        });
      }

    case REVERTED:
      {
        return merge(state, {
          activeFrom: null,
          nextValueCents: null
        });
      }

    case UNSET:
      {
        return merge(state, {
          activeFrom: action.payload.activeFrom,
          nextValueCents: null
        });
      }

    /* istanbul ignore next: default statement is here just in case */

    default:
      {
        return state;
      }
  }
}

function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 ? arguments[1] : undefined;

  if (action.error) {
    return state;
  }

  var _R$pathOr = pathOr({}, ['payload'], action),
      target = _R$pathOr.target,
      period = _R$pathOr.period;

  if (target && period) {
    return over(lensPath([target, period]), function (limit) {
      return limitReducer(limit, action);
    }, state);
  }

  if (action.type === CLEARED || action.type === SESSION_EXPIRED) {
    return {};
  }

  return state;
}

/** @module rushplay/compliance/limits */

export { load, toggleEdit, set, fetch, revert, submit, apply, clear, Limit$1 as Limit, reducer, getValidatorsForTarget, nextAmount, amount, isIncreased, isRemoved, isUnset, isLoaded, getLimit, hasLimit, APPLIED, CLEARED, DECREASED, EDITING_TOGGLED, INCREASED, LOADED, REVERTED, SAVED, UNSET };
